import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import jsPDF from 'jspdf';
import {
  ArrowLeft, FileText, Download, Printer, Mail, CheckCircle2, Loader2,
  User, MapPin, GraduationCap, ClipboardCheck, PenLine, X, Send, Save,
} from 'lucide-react';
import { BRAND } from '../mock/mock';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';
import {
  HCC_PROGRAMS, HCC_CAMPUSES, HCC_DLI, HCC_PUBLIC_EMAIL, HCC_WEBSITE,
  HCC_LOGO_URL, HCC_ACCREDITATION_LOGOS,
} from '../data/hccPrograms';

const PROVINCES_CA = ['BC', 'AB', 'SK', 'MB', 'ON', 'QC', 'NS', 'NB', 'PE', 'NL', 'YT', 'NT', 'NU'];
const TITLES = ['Mr.', 'Mrs.', 'Ms.', 'Mx.', 'Dr.', 'Other'];
const STATUSES = ['Canadian Citizen', 'Permanent Resident', 'Refugee / Protected Person', 'Other'];
const GENDERS = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const PROGRAM_NAMES = HCC_PROGRAMS.map((p) => `${p.name} — ${p.type}`);
const CAMPUS_NAMES = HCC_CAMPUSES.map((c) => c.name);

const blankForm = {
  counsellor: '', agency: '',
  title: '', last_name: '', first_name: '',
  street: '', city: '', province: 'BC', country: 'Canada', postal: '',
  email: '', phone: '', alt_phone: '',
  gender: '', status_in_country: '', dob: '',
  program_title: '', campus: '',
  program_length_hours: '', program_length_weeks: '', program_length_months: '', program_length_years: '',
  start_month: '', start_year: '',
  ack_id: false, ack_policies: false, ack_release: false, opt_marketing: false,
  applicant_name: '', signed_date: new Date().toISOString().slice(0, 10),
};

// ----- Image cache (DataURL) — used by jsPDF -----
const imgCache = {};
const loadImageAsDataURL = (url) => new Promise((resolve, reject) => {
  if (imgCache[url]) return resolve(imgCache[url]);
  const img = new Image();
  // Only request CORS for absolute external URLs; bundled (same-origin) assets don't need it
  if (typeof url === 'string' && /^https?:\/\//i.test(url)) {
    img.crossOrigin = 'anonymous';
  }
  img.onload = () => {
    try {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth || img.width;
      c.height = img.naturalHeight || img.height;
      c.getContext('2d').drawImage(img, 0, 0);
      const data = c.toDataURL('image/png');
      imgCache[url] = { data, w: c.width, h: c.height };
      resolve(imgCache[url]);
    } catch (e) { reject(e); }
  };
  img.onerror = reject;
  img.src = url;
});

// ----- UI atoms -----
const Field = ({ label, name, value, onChange, type = 'text', required, span = 1, ...rest }) => (
  <div className={span === 2 ? 'sm:col-span-2' : span === 3 ? 'sm:col-span-2 lg:col-span-3' : ''}>
    <label className="block text-[12px] font-semibold uppercase tracking-wider text-[#6b7e6d] mb-1">
      {label}{required && <span className="text-[#9b1c1c] ml-1">*</span>}
    </label>
    <input
      name={name} value={value} onChange={onChange} type={type}
      data-testid={`form-${name}`}
      className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 bg-white"
      {...rest}
    />
  </div>
);

const Select = ({ label, name, value, onChange, options, required, span = 1 }) => (
  <div className={span === 2 ? 'sm:col-span-2' : span === 3 ? 'sm:col-span-2 lg:col-span-3' : ''}>
    <label className="block text-[12px] font-semibold uppercase tracking-wider text-[#6b7e6d] mb-1">
      {label}{required && <span className="text-[#9b1c1c] ml-1">*</span>}
    </label>
    <select
      name={name} value={value} onChange={onChange}
      data-testid={`form-${name}`}
      className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 bg-white"
    >
      <option value="">— Select —</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const SectionHeader = ({ icon: Icon, title, n }) => (
  <div className="flex items-center gap-3 mt-2 mb-3">
    <div className="h-9 w-9 rounded-lg bg-[#0B5E2A] text-white flex items-center justify-center">
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#6b7e6d]">Section {n}</div>
      <h3 className="font-display text-lg text-[#0B5E2A]">{title}</h3>
    </div>
  </div>
);

const Check = ({ name, checked, onChange, children, required }) => (
  <label className="flex items-start gap-2.5 text-[13.5px] text-[#2e4030] leading-relaxed">
    <input
      type="checkbox" name={name} checked={checked} onChange={onChange}
      data-testid={`form-${name}`} className="mt-1 accent-[#0B5E2A]"
    />
    <span>{children}{required && <span className="text-[#9b1c1c] ml-1">*</span>}</span>
  </label>
);

// ----- PDF generation -----
const buildPdf = async (f, { blank = false } = {}) => {
  // Preload images once (used in real doc)
  const accreditation = await Promise.all(
    HCC_ACCREDITATION_LOGOS.map((l) => loadImageAsDataURL(l.url).catch(() => null)),
  );
  const collegeLogo = await loadImageAsDataURL(HCC_LOGO_URL).catch(() => null);

  // Selected campus (single-line)
  const selectedCampus = HCC_CAMPUSES.find((c) => c.name === f.campus) || HCC_CAMPUSES[0];

  // Layout constants (must match across measure + real pass)
  const headerH = 100;
  const titleH = 28;
  const footerH = 64;

  // Body renderer — works on any jsPDF instance, returns final y.
  // It contains all the drawing code so a measure pass and a real pass produce
  // identical heights without duplicating logic.
  const renderBody = (doc, startY) => {
    const W = doc.internal.pageSize.getWidth();
    const M = 40;
    const colW = (W - M * 2 - 16) / 2;
    const colX = [M, M + colW + 16];
    const fullW = W - M * 2;
    let y = startY;

    const drawSection = (title) => {
      doc.setFontSize(9.5); doc.setFont('helvetica', 'bold');
      doc.setTextColor(11, 60, 32);
      doc.text(title.toUpperCase(), M, y);
      doc.setDrawColor(240, 185, 60);
      doc.setLineWidth(1);
      doc.line(M, y + 4, W - M, y + 4);
      y += 11;
    };

    // Boxed input cell — label above + rounded rect with the value inside.
    const drawCell = (label, value, x, w) => {
      doc.setTextColor(107, 126, 109);
      doc.setFontSize(7.4); doc.setFont('helvetica', 'bold');
      doc.text(label.toUpperCase(), x + 2, y + 8);
      doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      const placeholder = blank ? '' : '—';
      const lines = doc.splitTextToSize(String(value || placeholder), w - 16);
      const boxH = Math.max(28, lines.length * 12 + 10);
      const boxTop = y + 12;
      doc.setDrawColor(196, 214, 198);
      doc.setFillColor(252, 253, 250);
      doc.setLineWidth(0.6);
      doc.roundedRect(x, boxTop, w, boxH, 3, 3, 'FD');
      doc.setTextColor(27, 42, 31);
      doc.text(lines, x + 8, boxTop + 15);
      return 12 + boxH;
    };

    const row2 = (left, right) => {
      const hL = left ? drawCell(left[0], left[1], colX[0], colW) : 0;
      const hR = right ? drawCell(right[0], right[1], colX[1], colW) : 0;
      y += Math.max(hL, hR) + 4;
    };
    const row1 = (label, value) => {
      const h = drawCell(label, value, M, fullW);
      y += h + 4;
    };
    const ack = (label, on) => {
      doc.setFontSize(8.6); doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(label, W - M * 2 - 22);
      doc.setFontSize(10); doc.setFont('helvetica', 'bold');
      doc.setTextColor(11, 60, 32);
      doc.text(on ? '[X]' : '[ ]', M, y + 8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 56, 44); doc.setFontSize(8.6);
      doc.text(lines, M + 18, y + 8);
      y += Math.max(14, lines.length * 11) + 2;
    };
    const sig = () => {
      doc.setTextColor(107, 126, 109); doc.setFontSize(7.4); doc.setFont('helvetica', 'bold');
      doc.text('SIGNED BY (TYPED FULL NAME)', colX[0] + 2, y + 8);
      doc.text('DATE', colX[1] + 2, y + 8);
      const boxTop = y + 12;
      const boxH = 32;
      doc.setDrawColor(196, 214, 198);
      doc.setFillColor(252, 253, 250);
      doc.setLineWidth(0.6);
      doc.roundedRect(colX[0], boxTop, colW, boxH, 3, 3, 'FD');
      doc.roundedRect(colX[1], boxTop, colW, boxH, 3, 3, 'FD');
      doc.setTextColor(27, 42, 31); doc.setFontSize(11); doc.setFont('helvetica', 'normal');
      const sigPh = blank ? '' : '—';
      doc.text(f.applicant_name || sigPh, colX[0] + 10, boxTop + 21);
      doc.text(f.signed_date || sigPh, colX[1] + 10, boxTop + 21);
      doc.setFontSize(7.5); doc.setTextColor(107, 126, 109); doc.setFont('helvetica', 'italic');
      doc.text('Typed signature recorded above.', colX[0] + 2, boxTop + boxH + 10);
      doc.setFont('helvetica', 'normal');
      y += 56;
    };

    // -------- Body --------
    // (No "Referral Agency" header — Counsellor/Agency render directly.)
    row2(['Counsellor', f.counsellor], ['Agency', f.agency]);
    y += 16;

    drawSection('Contact Information');
    row2(['Name', `${f.title} ${f.first_name} ${f.last_name}`.trim()], ['Date of Birth', f.dob]);
    row2(['Email', f.email], ['Phone', `${f.phone}${f.alt_phone ? `  /  ${f.alt_phone}` : ''}`]);
    row2(['Gender', f.gender || (blank ? '' : '—')], ['Status in Country', f.status_in_country || (blank ? '' : '—')]);
    row1('Mailing Address',
      `${f.street}, ${f.city}, ${f.province}, ${f.country} ${f.postal}`.replace(/^[ ,]+|[ ,]+$/g, ''));
    y += 16;

    drawSection('Program Information');
    const lenParts = [];
    if (f.program_length_hours) lenParts.push(`${f.program_length_hours} hours`);
    if (f.program_length_weeks) lenParts.push(`${f.program_length_weeks} weeks`);
    if (f.program_length_months) lenParts.push(`${f.program_length_months} months`);
    if (f.program_length_years) lenParts.push(`${f.program_length_years} years`);
    row2(['Program Title', f.program_title], ['Campus', f.campus]);
    row2(['Program Length', lenParts.join(' · ') || (blank ? '' : '—')],
         ['Start Date', `${f.start_month || ''} ${f.start_year || ''}`.trim() || (blank ? '' : '—')]);
    y += 16;

    drawSection('Application Checklist & Acknowledgements');
    ack('I have enclosed a copy of my valid photo ID.', f.ack_id);
    ack(`I confirm I have received program information, meet the entrance requirements, and ${BRAND.name} has provided me with the policies: Withdrawal/Refund, Admission, Prior Learning / Credit Transfer, Attendance, Dispute Resolution, Practicum / Co-Op, English Language Proficiency.`, f.ack_policies);
    ack(`I authorize ${BRAND.name} to obtain admission-requirement details and release application status to my agent (if applicable).`, f.ack_release);
    ack(`Add me to the ${BRAND.name} mailing list for promotions and updates. (Optional)`, f.opt_marketing);
    y += 16;

    drawSection('Applicant Signature');
    sig();

    return y;
  };

  // ---- Pass 1: measure body height (no header/footer needed) ----
  const measureDoc = new jsPDF({ unit: 'pt', format: 'letter' });
  const titleY = headerH + 2;
  const bodyTop = titleY + titleH + 4;
  const bodyBottom = measureDoc.internal.pageSize.getHeight() - footerH - 6;
  const available = bodyBottom - bodyTop;
  const finalY = renderBody(measureDoc, bodyTop);
  const actualBodyHeight = finalY - bodyTop;
  const padding = Math.max(6, (available - actualBodyHeight) / 2);

  // ---- Pass 2: real render ----
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;

  // Header
  doc.setFillColor(11, 60, 32);
  doc.rect(0, 0, W, headerH, 'F');
  doc.setFillColor(240, 185, 60);
  doc.rect(0, headerH, W, 2, 'F');
  if (collegeLogo) {
    const ratio = collegeLogo.w / collegeLogo.h;
    const lh = 64;
    const lw = lh * ratio;
    doc.addImage(collegeLogo.data, 'PNG', M, (headerH - lh) / 2, lw, lh);
  }
  // 4-line right side: name → address → contact → DLI
  doc.setTextColor(255);
  doc.setFontSize(15.5); doc.setFont('helvetica', 'bold');
  doc.text('Heritage Community College', W - M, 30, { align: 'right' });
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.setTextColor(220, 232, 222);
  doc.text(`${selectedCampus.name} Campus  ·  ${selectedCampus.address}`, W - M, 50, { align: 'right' });
  doc.setFontSize(9); doc.setTextColor(245, 250, 245);
  doc.text(`+1 (604) 593-5400   |   ${HCC_PUBLIC_EMAIL}   |   ${HCC_WEBSITE}`, W - M, 68, { align: 'right' });
  doc.setFontSize(9.5); doc.setFont('helvetica', 'bold');
  doc.setTextColor(247, 207, 110);
  doc.text(`DLI #${HCC_DLI}`, W - M, 86, { align: 'right' });

  // Title strip
  doc.setFillColor(254, 248, 230);
  doc.rect(0, titleY, W, titleH, 'F');
  doc.setTextColor(11, 60, 32);
  doc.setFontSize(12.5); doc.setFont('helvetica', 'bold');
  doc.text('DOMESTIC STUDENT APPLICATION FORM', M, titleY + 18);
  doc.setFontSize(8.2); doc.setFont('helvetica', 'normal');
  doc.setTextColor(107, 78, 0);
  if (!blank) {
    doc.text(`Submitted: ${new Date().toLocaleDateString()}`, W - M, titleY + 18, { align: 'right' });
  }

  // Body — vertically centred between titleStripEnd and footerTop
  renderBody(doc, bodyTop + padding);

  // Footer
  const fy = H - footerH;
  doc.setFillColor(245, 249, 243);
  doc.rect(0, fy, W, footerH, 'F');
  doc.setDrawColor(240, 185, 60); doc.setLineWidth(0.8);
  doc.line(0, fy, W, fy);
  const slots = HCC_ACCREDITATION_LOGOS.length;
  const slotW = (W - M * 2) / slots;
  // Per-logo max height + width-padding tuning. Non-circle (landscape) logos can grow taller
  // AND use almost the full slot width so they reach a similar visual weight to circular logos.
  // index: 0 BC Designated · 1 BC Care Aid · 2 NACC · 3 EQA · 4 StudentAid BC
  const perLogoMaxH = [50, 58, 50, 50, 58];
  const perLogoWidthPad = [16, 4, 16, 16, 4];
  accreditation.forEach((img, i) => {
    const cx = M + slotW * i + slotW / 2;
    const maxH = perLogoMaxH[i] || 50;
    const widthPad = perLogoWidthPad[i] ?? 16;
    if (img) {
      const ratio = img.w / img.h;
      let h = maxH; let w = h * ratio;
      if (w > slotW - widthPad) { w = slotW - widthPad; h = w / ratio; }
      doc.addImage(img.data, 'PNG', cx - w / 2, fy + (footerH - h) / 2, w, h);
    } else {
      doc.setTextColor(107, 126, 109); doc.setFontSize(7);
      doc.text(HCC_ACCREDITATION_LOGOS[i].label, cx, fy + footerH / 2 + 3, { align: 'center' });
    }
  });

  return doc;
};

const pdfToBase64 = async (f) => {
  const doc = await buildPdf(f);
  return doc.output('datauristring').split(',')[1];
};

// ----- Email modal -----
const EmailModal = ({ formData, onClose }) => {
  const { toast } = useToast();
  const [to, setTo] = useState(formData.email || '');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!to.trim()) { toast({ title: 'Enter an email address' }); return; }
    setBusy(true);
    try {
      const pdfBase64 = await pdfToBase64(formData);
      await api.post('/applications/email-form', {
        to: to.trim(), pdf_base64: pdfBase64,
        applicant_name: `${formData.first_name} ${formData.last_name}`.trim(),
      });
      setDone(true);
      toast({ title: 'Email sent', description: `Filled application sent to ${to.trim()}.` });
      setTimeout(onClose, 1800);
    } catch (err) {
      toast({ title: 'Failed to send', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl">
          <div className="border-b border-[#0B5E2A]/10 px-6 py-4 flex items-center justify-between">
            <h3 className="font-display text-xl text-[#0B5E2A]">Email Filled Form</h3>
            <button onClick={onClose} className="p-2 rounded hover:bg-[#f0f6ee]"><X className="h-5 w-5" /></button>
          </div>
          {done ? (
            <div className="p-8 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto brand-green" />
              <p className="mt-3 font-display text-lg text-[#0B5E2A]">Sent!</p>
            </div>
          ) : (
            <form onSubmit={send} className="p-6 space-y-4">
              <p className="text-sm text-[#4b5e4d]">We'll attach the filled form as a PDF and email it to:</p>
              <input
                type="email" value={to} onChange={(e) => setTo(e.target.value)}
                placeholder="recipient@example.com"
                data-testid="email-modal-to"
                className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30"
                required
              />
              <button type="submit" disabled={busy} data-testid="email-modal-send"
                className="w-full btn-green inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md disabled:opacity-60">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : <><Send className="h-4 w-4" /> Send Email</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

// ----- Page -----
const DomesticApplicationForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(blankForm);
  const [emailing, setEmailing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const printRef = useRef(null);

  // Pre-warm logo images so the first PDF render is instant
  useEffect(() => {
    [HCC_LOGO_URL, ...HCC_ACCREDITATION_LOGOS.map((l) => l.url)]
      .forEach((u) => loadImageAsDataURL(u).catch(() => {}));
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  // When user picks a program, surface the website-published length hint.
  const programHint = useMemo(() => {
    if (!form.program_title) return null;
    const namePart = form.program_title.split(' — ')[0].trim();
    return HCC_PROGRAMS.find((p) => `${p.name}`.trim() === namePart) || null;
  }, [form.program_title]);

  const validate = () => {
    const required = ['last_name', 'first_name', 'street', 'city', 'province', 'country', 'postal',
      'email', 'phone', 'dob', 'program_title', 'campus', 'applicant_name'];
    const missing = required.filter((k) => !String(form[k] || '').trim());
    if (missing.length) {
      toast({ title: 'Missing required fields', description: missing.slice(0, 4).join(', ') });
      return false;
    }
    if (!form.ack_policies || !form.ack_release) {
      toast({ title: 'Acknowledgements required', description: 'Please check the policies and authorization boxes.' });
      return false;
    }
    return true;
  };

  const onDownload = async () => {
    if (!validate()) return;
    const doc = await buildPdf(form);
    doc.save(`HCC-Application-${form.last_name || 'form'}.pdf`);
    toast({ title: 'PDF downloaded' });
  };

  const onDownloadBlank = async () => {
    // Truly empty form — no province/country defaults, no today's date.
    const emptyForm = Object.keys(blankForm).reduce((acc, k) => {
      acc[k] = typeof blankForm[k] === 'boolean' ? false : '';
      return acc;
    }, {});
    const doc = await buildPdf(emptyForm, { blank: true });
    doc.save('HCC-Domestic-Application-Blank.pdf');
    toast({ title: 'Blank form downloaded', description: 'Print and fill it physically.' });
  };

  const onPrint = () => {
    if (!validate()) return;
    window.print();
  };

  const onEmail = () => {
    if (!validate()) return;
    setEmailing(true);
  };

  const onSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const pdfBase64 = await pdfToBase64(form);
      const payload = {
        title: form.title, first_name: form.first_name, last_name: form.last_name,
        street: form.street, city: form.city, province: form.province,
        country: form.country, postal: form.postal,
        email: form.email, phone: form.phone, alt_phone: form.alt_phone,
        gender: form.gender, status_in_country: form.status_in_country, dob: form.dob,
        program_title: form.program_title, campus: form.campus,
        program_length_hours: String(form.program_length_hours || ''),
        program_length_weeks: String(form.program_length_weeks || ''),
        program_length_months: String(form.program_length_months || ''),
        program_length_years: String(form.program_length_years || ''),
        start_month: form.start_month, start_year: String(form.start_year || ''),
        counsellor: form.counsellor, heard_about: form.heard_about, agency: form.agency,
        ack_id: !!form.ack_id, ack_policies: !!form.ack_policies,
        ack_release: !!form.ack_release, opt_marketing: !!form.opt_marketing,
        applicant_name: form.applicant_name, signed_date: form.signed_date,
        pdf_base64: pdfBase64,
      };
      await api.post('/applications/domestic', payload);
      setSubmitted(true);
      toast({ title: 'Application submitted', description: 'HCC Admissions will contact you shortly.' });
    } catch (err) {
      toast({ title: 'Submission failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-[#fdfaf1] flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-white border border-[#0B5E2A]/10 rounded-2xl p-10 text-center shadow-sm">
          <CheckCircle2 className="h-14 w-14 mx-auto text-[#0B5E2A]" />
          <h2 className="mt-4 font-display text-2xl text-[#0B5E2A]">Application received</h2>
          <p className="mt-2 text-[#4b5e4d]">Thank you, {form.first_name}. The HCC Admissions team will review your application and reach out at <strong>{form.email}</strong>.</p>
          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            <button onClick={onDownload} data-testid="post-submit-download" className="btn-green inline-flex items-center gap-2 px-5 py-2.5 rounded-md">
              <Download className="h-4 w-4" /> Download My PDF
            </button>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A]">
              <ArrowLeft className="h-4 w-4" /> Home
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#fdfaf1]">
      {/* Header */}
      <header className="border-b border-[#0B5E2A]/10 bg-white/80 backdrop-blur sticky top-0 z-30 print:hidden">
        <div className="max-w-5xl mx-auto container-px h-[64px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="HCC" className="h-9 w-9 rounded-full" />
            <div className="leading-tight">
              <div className="font-display text-[15px] font-bold brand-green">{BRAND.name}</div>
              <div className="text-[9px] tracking-[0.22em] uppercase text-brand-gold font-semibold">{BRAND.full}</div>
            </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#4b5e4d] hover:text-[#0B5E2A]">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </div>
      </header>

      <section className="bg-[#f5f9f3] border-b border-[#0B5E2A]/10 print:hidden">
        <div className="max-w-5xl mx-auto container-px py-12">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-[#0B5E2A] text-[#F0B93C] flex items-center justify-center shadow-sm">
              <FileText className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <span className="eyebrow">For Domestic Students</span>
              <h1 className="mt-2 font-display text-3xl md:text-4xl text-[#0B5E2A]">Domestic Student Application</h1>
              <p className="mt-2 text-[#4b5e4d] max-w-2xl">Complete the form below. Submit it to HCC Admissions, or download / print / email a PDF copy.</p>
            </div>
            <button
              type="button"
              onClick={onDownloadBlank}
              data-testid="form-download-blank-btn"
              className="btn-green inline-flex items-center gap-2 px-5 py-2.5 rounded-md whitespace-nowrap shrink-0 self-start"
            >
              <Download className="h-4 w-4" /> Download Blank Form
            </button>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-5xl mx-auto container-px">
          <div ref={printRef} className="bg-white border border-[#0B5E2A]/10 rounded-2xl p-7 shadow-sm space-y-7" id="application-printable">
            <SectionHeader icon={MapPin} title="Referral Agency" n={1} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Counsellor" name="counsellor" value={form.counsellor} onChange={onChange} />
              <Field label="Agency" name="agency" value={form.agency} onChange={onChange} />
            </div>

            <SectionHeader icon={User} title="Contact Information" n={2} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Select label="Title" name="title" value={form.title} onChange={onChange} options={TITLES} />
              <Field label="Legal First Name(s)" name="first_name" value={form.first_name} onChange={onChange} required />
              <Field label="Last Name" name="last_name" value={form.last_name} onChange={onChange} required />
              <Field label="Street Number & Name" name="street" value={form.street} onChange={onChange} required span={2} />
              <Field label="City" name="city" value={form.city} onChange={onChange} required />
              <Select label="Province" name="province" value={form.province} onChange={onChange} options={PROVINCES_CA} required />
              <Field label="Country" name="country" value={form.country} onChange={onChange} required />
              <Field label="Postal / Zip Code" name="postal" value={form.postal} onChange={onChange} required />
              <Field label="Email Address" name="email" type="email" value={form.email} onChange={onChange} required />
              <Field label="Phone Number" name="phone" value={form.phone} onChange={onChange} required />
              <Field label="Alt Phone Number" name="alt_phone" value={form.alt_phone} onChange={onChange} />
              <Select label="Gender" name="gender" value={form.gender} onChange={onChange} options={GENDERS} />
              <Select label="Status in Country" name="status_in_country" value={form.status_in_country} onChange={onChange} options={STATUSES} />
              <Field label="Date of Birth" name="dob" type="date" value={form.dob} onChange={onChange} required />
            </div>

            <SectionHeader icon={GraduationCap} title="Program Information" n={3} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Select label="Program Title" name="program_title" value={form.program_title} onChange={onChange} options={PROGRAM_NAMES} required span={2} />
              <Select label="Campus" name="campus" value={form.campus} onChange={onChange} options={CAMPUS_NAMES} required />
            </div>

            {/* Program length sub-section */}
            <div className="rounded-xl border border-[#0B5E2A]/10 bg-[#f8fbf6] p-4">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="text-[12px] font-semibold uppercase tracking-wider text-[#0B5E2A]">Program Length</div>
                {programHint && (
                  <div data-testid="program-length-hint" className="text-[11.5px] text-[#6b7e6d]">
                    Per HCC website: <span className="font-semibold text-[#0B5E2A]">{programHint.length}</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Hours" name="program_length_hours" type="number" value={form.program_length_hours} onChange={onChange} min="0" />
                <Field label="Weeks" name="program_length_weeks" type="number" value={form.program_length_weeks} onChange={onChange} min="0" />
                <Field label="Months" name="program_length_months" type="number" value={form.program_length_months} onChange={onChange} step="0.1" min="0" />
                <Field label="Years" name="program_length_years" type="number" value={form.program_length_years} onChange={onChange} step="0.1" min="0" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Select label="Start Month" name="start_month" value={form.start_month} onChange={onChange} options={MONTHS} />
              <Field label="Start Year" name="start_year" type="number" value={form.start_year} onChange={onChange} min="2024" max="2099" />
            </div>

            <SectionHeader icon={ClipboardCheck} title="Application Checklist & Acknowledgements" n={4} />
            <div className="rounded-xl border border-[#F0B93C]/40 bg-[#FEF8E6] p-5 space-y-3">
              <Check name="ack_id" checked={form.ack_id} onChange={onChange}>
                I have enclosed a copy of my valid photo ID.
              </Check>
              <Check name="ack_policies" checked={form.ack_policies} onChange={onChange} required>
                I confirm that I have received a copy of the program information, meet the entrance requirements, and that {BRAND.name} has provided me with the following policies: <em>Student Withdrawal/Refund, Admission, Prior Learning Assessment / Credit Transfer, Attendance, Student Guidelines for Dispute Resolution, Work Experience: Practicum / Co-Op Work Term, English Language Proficiency.</em>
              </Check>
              <Check name="ack_release" checked={form.ack_release} onChange={onChange} required>
                I authorize {BRAND.name} to obtain any details relating to my admission requirements listed with this application, and to release information regarding the status of my application to my agent (if applicable).
              </Check>
              <Check name="opt_marketing" checked={form.opt_marketing} onChange={onChange}>
                Add me to the {BRAND.name} mailing list for promotions, program information and other services. <span className="text-[#6b7e6d]">(Optional)</span>
              </Check>
            </div>

            <SectionHeader icon={PenLine} title="Applicant Signature" n={5} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Signed by (typed full name)" name="applicant_name" value={form.applicant_name} onChange={onChange} required />
              <Field label="Date" name="signed_date" type="date" value={form.signed_date} onChange={onChange} required />
            </div>
          </div>

          {/* Action bar */}
          <div className="mt-6 flex flex-wrap gap-2 justify-end print:hidden">
            <button onClick={onPrint} data-testid="form-print-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee]">
              <Printer className="h-4 w-4" /> Print
            </button>
            <button onClick={onEmail} data-testid="form-email-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee]">
              <Mail className="h-4 w-4" /> Email
            </button>
            <button onClick={onDownload} data-testid="form-download-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee]">
              <Download className="h-4 w-4" /> Download PDF
            </button>
            <button onClick={onSubmit} disabled={submitting} data-testid="form-submit-btn"
              className="btn-green inline-flex items-center gap-2 px-5 py-2.5 rounded-md disabled:opacity-60">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : <><Save className="h-4 w-4" /> Submit Application</>}
            </button>
          </div>
        </div>
      </section>
      {emailing && <EmailModal formData={form} onClose={() => setEmailing(false)} />}
    </main>
  );
};

export default DomesticApplicationForm;
